import React from 'react';
import {
    AreaChart, Area, XAxis, YAxis, CartesianGrid,
    Tooltip, ResponsiveContainer, Legend,
} from 'recharts';

/**
 * MessageChart — Area chart showing inbound vs outbound messages over time.
 * Clean, minimal tooltip. No clutter.
 */
export default function MessageChart({ data }) {
    if (!data || (!data.inbound?.length && !data.outbound?.length)) {
        return (
            <div className="analytics-empty">
                <p>No message data yet</p>
            </div>
        );
    }

    // Merge inbound + outbound into a single dataset keyed by date
    const mergedMap = {};
    (data.inbound || []).forEach(d => {
        mergedMap[d.date] = { ...mergedMap[d.date], date: d.date, inbound: d.count };
    });
    (data.outbound || []).forEach(d => {
        mergedMap[d.date] = { ...mergedMap[d.date], date: d.date, outbound: d.count };
    });

    const chartData = Object.values(mergedMap)
        .map(d => ({ ...d, inbound: d.inbound || 0, outbound: d.outbound || 0 }))
        .sort((a, b) => a.date.localeCompare(b.date));

    // Format date for X axis labels
    const formatDate = (dateStr) => {
        try {
            const d = new Date(dateStr);
            return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        } catch {
            return dateStr;
        }
    };

    return (
        <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={chartData} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
                <defs>
                    <linearGradient id="inboundGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.15} />
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="outboundGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#6366f1" stopOpacity={0.15} />
                        <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                    </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis
                    dataKey="date"
                    tickFormatter={formatDate}
                    tick={{ fontSize: 11, fill: 'var(--text-secondary)' }}
                    axisLine={{ stroke: 'var(--border)' }}
                    tickLine={false}
                />
                <YAxis
                    tick={{ fontSize: 11, fill: 'var(--text-secondary)' }}
                    axisLine={false}
                    tickLine={false}
                    allowDecimals={false}
                />
                <Tooltip
                    contentStyle={{
                        background: 'var(--card-bg)',
                        border: '1px solid var(--border)',
                        borderRadius: '8px',
                        fontSize: '12px',
                        boxShadow: 'var(--shadow-md)',
                    }}
                    labelFormatter={formatDate}
                />
                <Legend
                    wrapperStyle={{ fontSize: '12px', paddingTop: '8px' }}
                    iconType="circle"
                    iconSize={8}
                />
                <Area
                    type="monotone"
                    dataKey="inbound"
                    name="Received"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    fill="url(#inboundGrad)"
                    animationDuration={600}
                />
                <Area
                    type="monotone"
                    dataKey="outbound"
                    name="Sent"
                    stroke="#6366f1"
                    strokeWidth={2}
                    fill="url(#outboundGrad)"
                    animationDuration={600}
                />
            </AreaChart>
        </ResponsiveContainer>
    );
}
